import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft } from 'react-icons/fi';
import { HelpCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationStore } from '@/common/store';
import { useCompanyStore } from '@/store/useCompanyStore';
import { NotificationIcon } from '@/common/components/notification/NotificationIcon';
import { NotificationData, NotificationType } from '@/common/components/notification/NotificationItem';
import { TabCounts } from '@/common/components/notification/NotificationList';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import Avatar from '@/common/components/avatar/Avatar';
import { typography } from '@/common/styles/typographyTokens';
import Tooltip from '@/common/components/tooltip/Tooltip';

const HeaderContainer = styled.header`
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  background-color: ${semanticColors.background.secondary};
  border-bottom: 1px solid ${semanticColors.border.primary};
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
`;

const PageTitle = styled.div`
  ${typography.heading.h6};
  color: ${semanticColors.text.secondary};
`;

const NotificationContainer = styled.div`
  margin-right: 0.5rem;
`;

const BackButtonContainer = styled.div`
  height: 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #9CA3AF;
  font-size: 0.875rem;
  cursor: pointer;
  gap: 0.25rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: #6B728060;
  }
`;

const BackButtonIcon = styled(FiChevronLeft)`
  font-size: 1.25rem;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const GuideButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px;
  margin-left: 12px;
  background-color: ${colorTokens.blue[50]};
  border: 1px solid ${colorTokens.blue[100]};
  border-radius: 13px;
  color: ${colorTokens.blue[600]};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  span {
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding-top: 1px;
  }

  &:hover {
    background-color: ${colorTokens.blue[100]};
    border-color: ${colorTokens.blue[200]};
    color: ${colorTokens.blue[700]};
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  }

  &:active {
    background-color: ${colorTokens.blue[200]};
    transform: scale(0.96);
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0.5rem;
`;

const CompanyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
`;

const CompanyName = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${semanticColors.text.primary};
  white-space: nowrap;
`;

const CompanyLabel = styled.span`
  font-size: 0.6875rem;
  color: ${colorTokens.gray[800]};
`;

/**
 * 알림 관련 Props - 외부에서 주입받음
 */
export interface NotificationProps {
  /** 현재 탭의 알림 목록 */
  notifications: NotificationData[];
  /** 각 탭별 미읽음 개수 */
  tabCounts: TabCounts;
  /** 전체 미읽음 개수 (헤더 배지용) */
  totalUnreadCount: number;
  /** 현재 선택된 탭 */
  currentTab: NotificationType;
  /** 로딩 상태 */
  isLoading: boolean;
  /** 더 불러올 데이터가 있는지 */
  hasMore: boolean;
  /** 검색 키워드 */
  searchKeyword: string;
  /** 모두 읽음 처리 핸들러 */
  onReadAll: () => void;
  /** 개별 알림 읽음 처리 핸들러 */
  onRead: (notification: NotificationData) => void;
  /** 탭 변경 핸들러 */
  onTabChange: (type: NotificationType) => void;
  /** 더 불러오기 핸들러 */
  onLoadMore: () => void;
  /** 알림 패널 열림 상태 변경 핸들러 */
  onOpenChange?: (isOpen: boolean) => void;
  /** 검색어 변경 핸들러 */
  onSearchChange: (keyword: string) => void;
}

export interface HeaderProps {
  /** 공간 타입 레이블 (예: '투자사 공간', '스타트업 공간') */
  spaceLabel?: string;
  /** 알림 테마 */
  notificationTheme?: 'investor' | 'startup';
  /** 알림 표시 여부 */
  showNotification?: boolean;
  /** 회사 정보 표시 여부 */
  showCompanyInfo?: boolean;
  /** 알림 관련 Props (외부에서 주입) */
  notificationProps?: NotificationProps;
}

const Header: React.FC<HeaderProps> = ({
  spaceLabel = '투자사 공간',
  notificationTheme = 'investor',
  showNotification = true,
  showCompanyInfo = true,
  notificationProps,
}) => {
  const { activeCompany } = useCompanyStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { headerTitle, higherMenu, backPath, restartIntro } = useNavigationStore();

  const handleBackClick = () => {
    if (!backPath) return;

    const targetPath = backPath.split('?')[0];
    navigate(backPath, { replace: location.pathname === targetPath });
  };

  const handleRestartIntro = () => {
    if (restartIntro) {
      restartIntro();
    }
  };

  // 알림 표시 조건: showNotification이 true이고, notificationProps가 주입되었을 때
  const shouldShowNotification = showNotification && notificationProps && activeCompany;

  return (
    <HeaderContainer>
      <LeftSection>
        <FlexRow>
          <BackButtonContainer>
            {higherMenu && backPath && (
              <BackButton onClick={handleBackClick}>
                <BackButtonIcon />
              </BackButton>
            )}
          </BackButtonContainer>
          <PageTitle>{headerTitle || ""}</PageTitle>
          {restartIntro && (
            <GuideButton onClick={handleRestartIntro}>
              <HelpCircle size={13} />
              <span>사용 가이드</span>
            </GuideButton>            
          )}
        </FlexRow>
      </LeftSection>
      
      <UserProfile>
        {shouldShowNotification && (
          <NotificationContainer>
            <NotificationIcon 
              notifications={notificationProps.notifications}
              tabCounts={notificationProps.tabCounts}
              totalUnreadCount={notificationProps.totalUnreadCount}
              currentTab={notificationProps.currentTab}
              isLoading={notificationProps.isLoading}
              hasMore={notificationProps.hasMore}
              searchKeyword={notificationProps.searchKeyword}
              theme={notificationTheme}
              onReadAll={notificationProps.onReadAll}
              onRead={notificationProps.onRead}
              onTabChange={notificationProps.onTabChange}
              onLoadMore={notificationProps.onLoadMore}
              onOpenChange={notificationProps.onOpenChange}
              onSearchChange={notificationProps.onSearchChange}
            />
          </NotificationContainer>
        )}
        {showCompanyInfo && activeCompany && (
          <Tooltip text={activeCompany.name || ''} id="company-name-tooltip">
            <CompanyInfo>
              <Avatar 
                src={activeCompany.logo} 
                alt={activeCompany.name || ''} 
                sizes="md"
                isUser={false}
              />
              <CompanyDetails>
                <CompanyName>{activeCompany.name}</CompanyName>
                <CompanyLabel>{spaceLabel}</CompanyLabel>
              </CompanyDetails>
            </CompanyInfo>
          </Tooltip>
        )}
      </UserProfile>
    </HeaderContainer>
  );
};

export default Header;
